import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';

import { getEnhancedAssetsStream, promoteEnhancedAssets } from 'apis/asset-repository-service/api';
import { Asset, AssetEnhancement, PromotedAsset } from 'apis/asset-repository-service/types';
import ErrorToastContent from 'common/components/ErrorToastContent';

export type ImageEnhancementMap = Record<Asset['id'], AssetEnhancement>;

type Props = {
  assetIds: Asset['id'][];
  templateId: string | null;
};

export const useListingManagerImageGalleryEnhancementsStream = ({ assetIds, templateId }: Props) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ['listing-manager-image-gallery-enhancements', assetIds.join('::'), templateId],
    [assetIds, templateId],
  );

  const { data: imageEnhancements, isFetching: isLoadingEnhancements } = useQuery({
    queryKey,
    queryFn: async (): Promise<ImageEnhancementMap | undefined> => {
      if (!templateId) {
        return;
      }
      await getEnhancedAssetsStream(
        { assetIds, templateId },
        {
          onMessage: (data: string) => {
            const enhacementUpdate: ImageEnhancementMap = {};

            data.split('\n').forEach(enhacementJSON => {
              if (!enhacementJSON) {
                return;
              }

              try {
                const assetEnhancement: AssetEnhancement = JSON.parse(enhacementJSON);
                enhacementUpdate[assetEnhancement.assetId] = assetEnhancement;
              } catch (error) {
                //
              }
            });

            queryClient.setQueryData(queryKey, (prev: ImageEnhancementMap) => ({
              ...prev,
              ...enhacementUpdate,
            }));
          },
        },
      );

      return queryClient.getQueryData(queryKey);
    },
    onError: error => {
      toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
    },
    retry: 0,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 60, // 1 hour based on ARS enhancement TTL
    refetchOnWindowFocus: false,
    enabled: Boolean(templateId) && assetIds.length > 0,
  });

  const { mutate: promoteEnhancements, isLoading: isPromotingEnhancements } = useMutation({
    mutationKey: queryKey,
    mutationFn: async () => {
      if (!templateId || assetIds.length < 1) {
        return;
      }

      return promoteEnhancedAssets(assetIds, templateId);
    },
    onSuccess: (enhancedAssets: PromotedAsset[], onSuccess: (enhancedAssets: PromotedAsset[]) => void) => {
      toast.success(
        intl.formatMessage({ id: 'shared-menu.editor.image-gallery.edit-images.toasts.new-ai-images-added' }),
      );
      onSuccess(enhancedAssets);
    },
    onError: error => {
      toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
    },
  });

  return {
    imageEnhancements: imageEnhancements || {},
    promoteEnhancements,
    isLoadingEnhancements,
    isPromotingEnhancements,
  };
};
